/* eslint-disable complexity */
/* eslint-disable max-params */
import { ACCESS_LEVEL, SERIES_TO_USER_LEVEL_PERMISSION_KEYS } from '../config/constants';
import type { TRule, ValueOf, TParsedPermission } from '../types';

import {
  STAGES_ACCESS_MAP,
  GAME_ACTIONS_ACCESS_MAP,
  SERIES_ACTIONS_ACTION_MAP,
} from '../config/permissionsMap';

export type TPermissionDefinition = {
  ALLOW?: string[];
  DENY?: string[];
  OVERRIDE?: string[];
};

export type TAccessMap = {
  [key: string]: { allow?: string[]; deny?: string[] };
};

export const haveAccess = (
  permissionsToCheck: string[] = [],
  globalPermissions: TPermissionDefinition = {},
  contextPermissions: TPermissionDefinition = {}
) => {
  const {
    ALLOW: globalAllow = [],
    DENY: globalDeny = [],
    OVERRIDE: globalOverride = [],
  } = globalPermissions;

  const { ALLOW: contextAllow = [], DENY: contextDeny = [] } = contextPermissions;

  const allowedPermissions = [
    ...globalOverride,
    ...globalAllow.filter(permission => !contextDeny.includes(permission)),
    ...contextAllow.filter(permission => !globalDeny.includes(permission)),
  ];

  return permissionsToCheck.some(permission => allowedPermissions.includes(permission));
};

export const checkAccessByPermissionsMap = ({
  globalPermissions,
  contextPermissions,
  permissions: { allow = [], deny = [] },
  isSiteOwner,
}: {
  globalPermissions: TPermissionDefinition;
  contextPermissions: TPermissionDefinition;
  permissions: { allow?: string[]; deny?: string[] };
  isSiteOwner?: boolean;
}) => {
  if (isSiteOwner) {
    return true;
  } else if (!(allow.length || deny.length)) {
    return true;
  } else if (allow.length && deny.length) {
    return (
      haveAccess(allow, globalPermissions, contextPermissions) &&
      !haveAccess(deny, globalPermissions, contextPermissions)
    );
  } else if (allow.length) {
    return haveAccess(allow, globalPermissions, contextPermissions);
  } else {
    return !haveAccess(deny, globalPermissions, contextPermissions);
  }
};

function createHasAccessChecker(ACCESS_MAP: TAccessMap) {
  return function (
    accessType: string,
    globalPermissions: TPermissionDefinition,
    contextPermissions: TPermissionDefinition,
    { isSiteOwner }: { isSiteOwner?: boolean } = {}
  ) {
    return checkAccessByPermissionsMap({
      permissions: ACCESS_MAP[accessType],
      globalPermissions,
      contextPermissions,
      isSiteOwner,
    });
  };
}

export const haveStageAccess = createHasAccessChecker(STAGES_ACCESS_MAP);

export const hasGameActionAccess = createHasAccessChecker(GAME_ACTIONS_ACCESS_MAP);

export const hasSeriesActionAccess = createHasAccessChecker(SERIES_ACTIONS_ACTION_MAP);

export const parseScopedPermissions = ({
  permissions,
  userId,
  globalPermissions: { OVERRIDE = [] } = {},
}: {
  permissions: { rules: TRule[] };
  userId: string;
  globalPermissions?: TPermissionDefinition;
}) => {
  const parsedPermissions = {
    ALLOW: [],
    DENY: [],
  } as TParsedPermission;
  const { rules = [] } = permissions;

  rules.forEach(({ permission, rule: { accessType, userids = [] } = {} }: TRule) => {
    if (accessType === 'ALL' || OVERRIDE.includes(permission)) {
      parsedPermissions.ALLOW.push(permission);
    } else {
      userids.includes(userId)
        ? parsedPermissions.ALLOW.push(permission)
        : parsedPermissions.DENY.push(permission);
    }
  });
  return parsedPermissions;
};

export const parseSeriesPermissions = ({
  permissions,
  userId,
  userLevelPermissions: { OVERRIDE = [] } = {},
}: {
  permissions: { rules: TRule[] };
  userId: string;
  userLevelPermissions?: { OVERRIDE?: string[] };
}) => {
  const parsedPermissions = {
    ALLOW: [],
    DENY: [],
  } as TParsedPermission;
  const { rules = [] } = permissions;

  rules.forEach(({ permission, rule: { accessType, userids = [] } = {} }: TRule) => {
    const permissionKey = SERIES_TO_USER_LEVEL_PERMISSION_KEYS[permission] || permission;
    if (accessType === 'ALL' || OVERRIDE.includes(permissionKey)) {
      parsedPermissions.ALLOW.push(permissionKey);
    } else {
      userids.includes(userId)
        ? parsedPermissions.ALLOW.push(permissionKey)
        : parsedPermissions.DENY.push(permissionKey);
    }
  });
  return parsedPermissions;
};

export const getRolesIdMapForPermissionsSet = (
  allRoles: {
    [key: string]: {
      id: string;
      permissions: {
        [key: string]: ValueOf<typeof ACCESS_LEVEL>;
      };
    };
  },
  permissionsToCheck: string[]
) => {
  const filteredRoles = Object.values(allRoles)
    .filter(({ permissions }) => {
      const availablePermissions = Object.keys(permissions).filter(permission =>
        permissionsToCheck.includes(permission)
      );
      if (availablePermissions.length === 0) return false;

      return availablePermissions.some(permission => permissions[permission] !== ACCESS_LEVEL.DENY);
    })
    .map(({ id }) => id);
  return filteredRoles;
};
